import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useEffect, useRef, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import {
	useCreateDsm5TeenMutation,
	useEditDsm5TeenMutation,
	useGetDsm5TeenDraftQuery,
} from '@/api/forms-management/dsm5Teen';
import { FormManagementStatus } from '@/api/enum/common.enum';
import { useGetAllEnumListQuery } from '@/api/common';
import EditDsm5TeenForm from './components/EditDsm5TeenForm';

export default function CreateDsm5TeenPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.create);
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list') ? 'admission-request-list' : 'member-list';
	const [createDsm5Teen] = useCreateDsm5TeenMutation();
	const { preAdmissionId, formType, admissionRequestId } = useParams();
	const showModal = useModal();
	const [editDsm5Teen] = useEditDsm5TeenMutation();

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: details }: any = useGetDsm5TeenDraftQuery(
		{ id: preAdmissionId || admissionRequestId || '', type: formType || '' },
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const dsm5TeenRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);

	const handleSubmit = async (type: string) => {
		const results = await Promise.all([
			dsm5TeenRef.current?.validateForm() || Promise.resolve(false),
		]);

		console.log(results);

		const validate = results.some((result) => result === false);
		if (type == 'verify') {
			enqueueSnackbar(
				validate
					? t('snackbar.fieldsVerifiedFalse')
					: t('snackbar.fieldsVerifiedTrue'),
				{
					variant: validate ? 'error' : 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			return;
		}
		if (validate) return;

		const {
			branchId,
			userId,
			nameZh,
			fillingDate,
			answerList,
			answerList2,
			relationship,
			spentTime,
		} = {
			...(await dsm5TeenRef.current?.getFormData()),
		};

		const scores = answerList
			.map((row: any) => {
				const keys = ['answer0', 'answer1', 'answer2', 'answer3', 'answer4'];
				const trueKey = keys.find((key) => row[key] === true);
				return trueKey ? parseInt(trueKey.replace('answer', '')) : null;
			})
			.filter((score: any) => score !== null);
		const scores2 = answerList2
			.map((row: any) => {
				const keys = ['answer0', 'answer1'];
				const trueKey = keys.find((key) => row[key] === true);
				return trueKey ? parseInt(trueKey.replace('answer', '')) : null;
			})
			.filter((score: any) => score !== null);

		const formData = {
			branchId,
			userId,
			nameZh,
			fillingDate,
			relationship,
			spentTime,
			answerListPartOne: scores,
			answerListPartTwo: scores2,

			id: details.id || '',
			formStatus:
				type == 'draft'
					? FormManagementStatus.draft
					: FormManagementStatus.submitted,
		};

		if (!formData.id) delete formData.id;

		if (type == 'submit') {
			showModal((props: any) => (
				<ConfirmationDialog
					{...props}
					showCloseIcon={false}
					title={t('preAdmission.completeFormTitle')}
					titleProps={{ variant: 'h6' }}
					description={
						<Typography variant='body1'>
							{t('preAdmission.completeFormText')}
						</Typography>
					}
					cancellationText={t('button.cancel')}
					confirmationText={t('button.confirm')}
					confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
					otherButtonText={t('button.saveDraft')}
					otherButtonProps={{ type: 'submit', variant: 'contained' }}
					onConfirm={async () => {
						saveApi({
							...formData,
							formStatus: FormManagementStatus.submitted,
						});
					}}
					onOtherConfirm={async () => {
						saveApi({ ...formData, formStatus: FormManagementStatus.draft });
					}}
				/>
			));
		} else if (type == 'draft') {
			saveApi({ ...formData, formStatus: FormManagementStatus.draft });
		}
	};

	const saveApi = async (formData: any) => {
		const response = details.id
			? await editDsm5Teen(formData)
			: await createDsm5Teen(formData);
		if ('data' in response) {
			enqueueSnackbar(
				formData.formStatus == FormManagementStatus.submitted
					? t('snackbar.faceSheetSubmitSuccess')
					: t('snackbar.draftFaceSheetSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			navigate(
				preAdmissionId
					? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
					: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`
			);
		}
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Card sx={{ marginTop: 3 }}>
					<CardContent sx={{ padding: '24px' }}>
						<Typography variant='h4' marginBottom={'38px'}>
							{t('drawerListItems.newDsm5Teen')}
						</Typography>
						<EditDsm5TeenForm
							ref={dsm5TeenRef}
							type={type}
							setType={setType}
							details={details?.data}
							allEnumList={allEnumList}
						/>
					</CardContent>

					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button
							type='submit'
							onClick={() =>
								navigate(
									preAdmissionId
										? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
										: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`
								)
							}
						>
							{t('button.cancel')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('draft')}
							endIcon={<EditNoteOutlinedIcon />}
						>
							{t('button.saveDraft')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('verify')}
							endIcon={<TaskOutlinedIcon />}
						>
							{t('button.verifyFields')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('submit')}
							endIcon={<CheckCircleOutlineIcon />}
						>
							{t('button.complete')}
						</Button>
					</CardActions>
				</Card>
			</Box>
		</>
	);
}
